import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import type { Address } from "viem"
import { usePriceHistory } from "../../hooks/market/usePriceHistory"
import { BINARY_LABELS } from "../../lib/constants"
import type { OutcomeBucket } from "../../lib/types"
import { Skeleton } from "../ui/Skeleton"

// Update interval for live chart time progression (ms)
const LIVE_TIME_UPDATE_INTERVAL = 60_000 // 60 seconds - price data updates more frequently via usePriceHistory

interface PriceChartProps {
	marketAddress: Address | undefined
	/** Outcome buckets with labels and directions - passed from parent to avoid redundant fetching */
	buckets: OutcomeBucket[]
	/** Current probabilities for each bucket (0-1 range) - passed from parent */
	probabilities: number[]
	/** Whether the market is still live (for pulsing dot animation) */
	isLive?: boolean
	/** Market open time in milliseconds (JS Date.getTime() format) - chart starts here */
	marketOpenTimeMs?: number
	/** Market close time in milliseconds (JS Date.getTime() format) - chart ends here for ended markets */
	marketCloseTimeMs?: number
}

const CHART_COLORS = [
	"#00D952", // Brand Neon Green (Long/Yes)
	"#ef4444", // Red (Short/No)
	"#F200FF", // Brand Neon Magenta (Alt/3rd)
	"#3b82f6", // Blue
	"#f59e0b", // Amber
	"#10b981", // Emerald
]

function getOutcomeColor(index: number): string {
	return CHART_COLORS[index % CHART_COLORS.length] ?? "#6b7280"
}

// Fixed y-axis range: always show full 0-100% with 25% intervals
const Y_AXIS = { min: 0, max: 1, ticks: [0, 0.25, 0.5, 0.75, 1] } as const

// Line rendering configuration
const LINE_CONFIG = {
	strokeWidth: 1.5,
	minHorizontalDist: 2, // Skip horizontal segments shorter than this
} as const

// Calculate y-offset for a line to prevent overlap when prices are similar
// Spread increases with more outcomes to keep lines distinguishable
function getLineYOffset(bucketIndex: number, totalBuckets: number): number {
	if (totalBuckets <= 1) {
		return 0
	}
	// Spread lines evenly: more buckets = wider total spread
	// Binary: ±1.5px, 3 outcomes: ±2px each, 5 outcomes: ±2px each
	const spreadPerLine = Math.min(2, 3 / totalBuckets)
	const totalSpread = spreadPerLine * (totalBuckets - 1)
	// Center around 0: first bucket at -totalSpread/2, last at +totalSpread/2
	return -totalSpread / 2 + bucketIndex * spreadPerLine
}

// Chart dimensions - Y-axis labels on right, inside viewBox
const CHART_DIMENSIONS = {
	width: 320,
	height: 200,
	padding: { top: 16, right: 32, bottom: 24, left: 12 },
} as const

// Format timestamp for hover display
function formatHoverTime(ts: number): string {
	return new Date(ts).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })
}

export const PriceChart = memo(function PriceChart({
	marketAddress,
	buckets,
	probabilities: currentPrices,
	isLive = true,
	marketOpenTimeMs,
	marketCloseTimeMs,
}: PriceChartProps) {
	// Always show full market history - markets are 1 week long
	const { data, isLoading, error } = usePriceHistory(
		marketAddress
			? {
					marketAddress,
					bucketCount: buckets.length,
					timeRange: "MAX",
				}
			: null,
	)

	const [hoveredX, setHoveredX] = useState<number | null>(null)
	const [isDesktop, setIsDesktop] = useState(false)
	const [currentTimeMs, setCurrentTimeMs] = useState(Date.now())
	const svgRef = useRef<SVGSVGElement>(null)

	// Detect desktop for showing more x-axis labels
	useEffect(() => {
		const checkDesktop = () => setIsDesktop(window.innerWidth >= 640)
		checkDesktop()
		window.addEventListener("resize", checkDesktop)
		return () => window.removeEventListener("resize", checkDesktop)
	}, [])

	// Update current time periodically for live markets to progress the chart
	useEffect(() => {
		if (!isLive) {
			return
		}
		const interval = setInterval(() => {
			setCurrentTimeMs(Date.now())
		}, LIVE_TIME_UPDATE_INTERVAL)
		return () => clearInterval(interval)
	}, [isLive])

	const { width, height, padding } = CHART_DIMENSIONS
	const chartWidth = width - padding.left - padding.right
	const chartHeight = height - padding.top - padding.bottom

	const points = data?.points ?? []

	// Get display prices - use currentPrices for the latest values if available
	const latestPrices = useMemo(() => {
		if (currentPrices && currentPrices.length > 0) {
			const prices: Record<number, number> = {}
			currentPrices.forEach((p, i) => {
				prices[i] = p
			})
			return prices
		}
		if (points.length === 0) {
			// Return equal probability for empty state
			const equalPrices: Record<number, number> = {}
			for (let i = 0; i < buckets.length; i++) {
				equalPrices[i] = 1 / buckets.length
			}
			return equalPrices
		}
		return points[points.length - 1]?.prices ?? {}
	}, [points, currentPrices, buckets.length])

	// Calculate chart data with dynamic y-axis
	const chartData = useMemo(() => {
		// All timestamps in this component are in milliseconds (JS Date.getTime() format)
		// Use currentTimeMs state for live markets so the chart updates as time progresses
		const nowMs = currentTimeMs
		const isMarketEnded = marketCloseTimeMs != null && nowMs >= marketCloseTimeMs

		// Equal probability prices (50/50 for binary, 1/n for n outcomes)
		const equalProbabilityPrices: Record<number, number> = {}
		for (let i = 0; i < buckets.length; i++) {
			equalProbabilityPrices[i] = 1 / buckets.length
		}

		// Start with all data points - don't filter, show everything
		let displayPoints = [...points]

		// Determine chart time boundaries from actual data
		const dataStartMs = displayPoints[0]?.timestampMs
		const dataEndMs = displayPoints[displayPoints.length - 1]?.timestampMs

		// Chart start: earliest of market open or first data point
		const chartStartTimeMs = marketOpenTimeMs
			? Math.min(marketOpenTimeMs, dataStartMs ?? marketOpenTimeMs)
			: (dataStartMs ?? nowMs - 60 * 60 * 1000)

		// Chart end: for ended markets use cutoff or last data point (whichever is later)
		// For live markets use current time
		const chartEndTimeMs = isMarketEnded ? Math.max(marketCloseTimeMs ?? 0, dataEndMs ?? 0) : nowMs

		// Add synthetic start point at chart start if first data point is later
		const firstPoint = displayPoints[0]
		if (!firstPoint || firstPoint.timestampMs > chartStartTimeMs) {
			displayPoints = [
				{
					timestampMs: chartStartTimeMs,
					blockNum: "0",
					prices: { ...equalProbabilityPrices },
				},
				...displayPoints,
			]
		}

		// Add synthetic end point with current prices if needed
		const lastPoint = displayPoints[displayPoints.length - 1]
		if (lastPoint && lastPoint.timestampMs < chartEndTimeMs) {
			const endPrices: Record<number, number> = {}
			if (currentPrices && currentPrices.length > 0) {
				currentPrices.forEach((p, i) => {
					endPrices[i] = p
				})
			} else {
				for (let i = 0; i < buckets.length; i++) {
					endPrices[i] = lastPoint.prices[i] ?? equalProbabilityPrices[i] ?? 1 / buckets.length
				}
			}
			displayPoints = [
				...displayPoints,
				{
					timestampMs: chartEndTimeMs,
					blockNum: isMarketEnded ? "end" : "now",
					prices: endPrices,
				},
			]
		}

		// For binary markets, enforce symmetry: prices must sum to 1
		// This ensures the chart shows proper probabilities (bucket1 = 1 - bucket0)
		if (buckets.length === 2) {
			displayPoints = displayPoints.map(point => {
				const price0 = Math.min(1, Math.max(0, point.prices[0] ?? 0.5))
				return {
					...point,
					prices: { 0: price0, 1: 1 - price0 },
				}
			})
		}

		// Use chart boundaries for x-axis
		const minTime = chartStartTimeMs
		const maxTime = chartEndTimeMs
		const timeSpan = maxTime - minTime

		// Create line data for each bucket
		const lines = buckets.map((bucket, bucketIndex) => {
			const color = getOutcomeColor(bucketIndex)
			const pathPoints = displayPoints.map(point => {
				const x =
					timeSpan > 0
						? padding.left + ((point.timestampMs - minTime) / timeSpan) * chartWidth
						: padding.left + chartWidth / 2
				const price = point.prices[bucketIndex] ?? 0
				// Map price to y position (0-1 range, inverted for SVG coordinates)
				const y = padding.top + (1 - price) * chartHeight
				return { x, y, price, timestampMs: point.timestampMs }
			})
			return { bucket, bucketIndex, color, pathPoints }
		})

		// Generate evenly spaced time labels like Polymarket
		const timeLabels: { x: number; label: string; anchor: "start" | "middle" | "end" }[] = []
		const dayMs = 24 * 60 * 60 * 1000

		// Format based on time span granularity - keep labels short
		const formatTimestamp = (ts: number): string => {
			const date = new Date(ts)
			if (timeSpan < dayMs) {
				// Show compact time: "9:30pm" style
				const hours = date.getHours()
				const mins = date.getMinutes().toString().padStart(2, "0")
				const ampm = hours >= 12 ? "pm" : "am"
				const h = hours % 12 || 12
				return `${h}:${mins}${ampm}`
			}
			if (timeSpan < 7 * dayMs) {
				return date.toLocaleDateString([], { weekday: "short" })
			}
			return date.toLocaleDateString([], { month: "short", day: "numeric" })
		}

		// Evenly space labels across the chart width (like Polymarket)
		if (timeSpan > 0) {
			// More labels on desktop, fewer on mobile for readability
			const labelCount = isDesktop ? 5 : 3

			for (let i = 0; i < labelCount; i++) {
				// Calculate position as fraction of chart width
				const fraction = i / (labelCount - 1)
				const x = padding.left + fraction * chartWidth
				const timestamp = minTime + fraction * timeSpan

				// Determine text anchor: start for first, end for last, middle for others
				const anchor = i === 0 ? "start" : i === labelCount - 1 ? "end" : "middle"

				timeLabels.push({
					x,
					label: formatTimestamp(timestamp),
					anchor,
				})
			}
		}

		return { lines, timeSpan, timeLabels }
	}, [
		points,
		buckets,
		chartWidth,
		chartHeight,
		currentPrices,
		marketOpenTimeMs,
		marketCloseTimeMs,
		isDesktop,
		currentTimeMs,
		padding.left,
		padding.top,
	])

	// Get price at a specific x position (interpolated for step chart)
	const getPriceAtX = useCallback((line: (typeof chartData.lines)[0], targetX: number) => {
		if (!line || line.pathPoints.length === 0) {
			return null
		}

		// Find the point just before or at targetX (step chart shows previous value)
		let lastPoint = line.pathPoints[0]
		for (const point of line.pathPoints) {
			if (point.x <= targetX) {
				lastPoint = point
			} else {
				break
			}
		}
		return lastPoint
	}, [])

	// Convert client X position to chart X coordinate
	const clientXToChartX = useCallback(
		(clientX: number) => {
			if (!svgRef.current) {
				return null
			}
			const rect = svgRef.current.getBoundingClientRect()
			const relativeX = clientX - rect.left
			const scaleX = width / rect.width
			const scaledX = relativeX * scaleX
			// Clamp to chart area
			return Math.max(padding.left, Math.min(padding.left + chartWidth, scaledX))
		},
		[chartWidth, padding.left, width],
	)

	const handleMouseMove = useCallback(
		(e: React.MouseEvent<SVGSVGElement>) => {
			if (!svgRef.current || chartData.lines.length === 0) {
				return
			}
			const x = clientXToChartX(e.clientX)
			if (x !== null) {
				setHoveredX(x)
			}
		},
		[chartData.lines, clientXToChartX],
	)

	const handleTouchStart = useCallback(
		(e: React.TouchEvent<SVGSVGElement>) => {
			if (!svgRef.current || chartData.lines.length === 0) {
				return
			}
			const touch = e.touches[0]
			if (!touch) {
				return
			}
			const x = clientXToChartX(touch.clientX)
			if (x !== null) {
				setHoveredX(x)
			}
		},
		[chartData.lines, clientXToChartX],
	)

	const handleTouchMove = useCallback(
		(e: React.TouchEvent<SVGSVGElement>) => {
			if (!svgRef.current || chartData.lines.length === 0) {
				return
			}
			const touch = e.touches[0]
			if (!touch) {
				return
			}
			const x = clientXToChartX(touch.clientX)
			if (x !== null) {
				setHoveredX(x)
			}
		},
		[chartData.lines, clientXToChartX],
	)

	const handleMouseLeave = useCallback(() => setHoveredX(null), [])
	const handleTouchEnd = useCallback(() => setHoveredX(null), [])

	// Calculate hovered data for all lines
	const hoveredData = useMemo(() => {
		if (hoveredX === null || chartData.lines.length === 0) {
			return null
		}

		const firstLine = chartData.lines[0]
		if (!firstLine) {
			return null
		}

		const firstPoint = getPriceAtX(firstLine, hoveredX)
		if (!firstPoint) {
			return null
		}

		// Get y positions and prices for all lines at this x
		const lineData = chartData.lines.map(line => {
			const point = getPriceAtX(line, hoveredX)
			return {
				bucketIndex: line.bucketIndex,
				bucket: line.bucket,
				color: line.color,
				price: point?.price ?? 0,
				y: point?.y ?? 0,
			}
		})

		// Sort by y position (top to bottom) for label stacking
		const sortedByY = [...lineData].sort((a, b) => a.y - b.y)

		return {
			x: hoveredX,
			timestampMs: firstPoint.timestampMs,
			lineData,
			sortedByY,
		}
	}, [hoveredX, chartData.lines, getPriceAtX])

	// Loading state - only show skeleton when no data at all
	if (isLoading && !data) {
		return (
			<div className="w-full">
				<ChartLegendSkeleton bucketCount={buckets.length} labels={buckets.map(b => b.label)} />
				<Skeleton className="aspect-[320/200] w-full rounded-xl" />
			</div>
		)
	}

	// Error state
	if (error) {
		return (
			<div className="w-full">
				<Legend buckets={buckets} prices={{}} />
				<div className="flex aspect-[320/200] items-center justify-center rounded-xl border border-border-subtle border-dashed bg-bg-secondary">
					<span className="text-text-muted">Failed to load chart</span>
				</div>
			</div>
		)
	}

	// For empty state (no trades), chartData will generate synthetic points at equal probability
	// This shows flat lines at 1/n for n outcomes, which is more informative than a blank state

	return (
		<div className="w-full">
			<Legend
				buckets={buckets}
				hoveredPrices={
					hoveredData
						? Object.fromEntries(hoveredData.lineData.map(l => [l.bucketIndex, l.price]))
						: undefined
				}
				prices={latestPrices}
			/>

			{/* Fixed aspect ratio prevents layout shift during data transitions */}
			<div className="relative aspect-[320/200] w-full">
				<svg
					aria-label="Price history chart showing outcome probabilities over time"
					className="absolute inset-0 size-full"
					onMouseLeave={handleMouseLeave}
					onMouseMove={handleMouseMove}
					onTouchEnd={handleTouchEnd}
					onTouchMove={handleTouchMove}
					onTouchStart={handleTouchStart}
					preserveAspectRatio="xMidYMid meet"
					ref={svgRef}
					role="img"
					style={{ touchAction: "pan-y" }}
					viewBox={`0 0 ${width} ${height}`}
				>
					{/* Transparent background for unboxed chart */}
					<rect fill="transparent" height={height} rx={0} width={width} />

					{/* Grid lines and Y-axis labels */}
					{Y_AXIS.ticks.map((tick, i) => {
						const y = padding.top + (1 - tick) * chartHeight
						return (
							// biome-ignore lint/suspicious/noArrayIndexKey: tick indices are stable
							<g key={i}>
								<line
									className="stroke-border-subtle"
									strokeWidth={0.5}
									x1={padding.left}
									x2={width - padding.right}
									y1={y}
									y2={y}
								/>
								<text
									className="fill-text-secondary text-[9px]"
									dominantBaseline="middle"
									textAnchor="end"
									x={width - 4}
									y={y}
								>
									{(tick * 100).toFixed(0)}%
								</text>
							</g>
						)
					})}

					{/* X-axis labels - evenly spaced */}
					{chartData.timeLabels.map(label => (
						<text
							className="fill-text-secondary text-[11px] sm:text-[8px]"
							key={label.x}
							textAnchor={label.anchor}
							x={label.x}
							y={height - 6}
						>
							{label.label}
						</text>
					))}

					{/* Step Lines - split at hover point for fading effect */}
					{/* Render lines in reverse order so first line (Long/blue) is on top */}
					{[...chartData.lines].reverse().map(line => {
						if (line.pathPoints.length < 2) {
							return null
						}

						// Find the split point based on hover position
						const splitX = hoveredX ?? padding.left + chartWidth

						// Y offset to prevent overlap when lines are at same price
						const yOffset = getLineYOffset(line.bucketIndex, buckets.length)

						// Build step path: horizontal to X, then vertical to Y
						const beforeParts: string[] = []
						const afterParts: string[] = []
						let lastX = 0
						let lastY = 0
						let foundSplit = false

						line.pathPoints.forEach((point, i) => {
							const adjustedY = point.y + yOffset

							if (i === 0) {
								beforeParts.push(`M ${point.x} ${adjustedY}`)
								lastX = point.x
								lastY = adjustedY
							} else if (!foundSplit && point.x <= splitX) {
								// Skip tiny horizontal movements to avoid visual jogs
								if (point.x - lastX >= LINE_CONFIG.minHorizontalDist) {
									beforeParts.push(`H ${point.x}`)
									lastX = point.x
								}
								beforeParts.push(`V ${adjustedY}`)
								lastY = adjustedY
							} else {
								if (!foundSplit) {
									beforeParts.push(`H ${splitX}`)
									afterParts.push(`M ${splitX} ${lastY}`)
									if (point.x - splitX >= LINE_CONFIG.minHorizontalDist) {
										afterParts.push(`H ${point.x}`)
										lastX = point.x
									}
									afterParts.push(`V ${adjustedY}`)
									foundSplit = true
								} else {
									if (point.x - lastX >= LINE_CONFIG.minHorizontalDist) {
										afterParts.push(`H ${point.x}`)
										lastX = point.x
									}
									afterParts.push(`V ${adjustedY}`)
								}
								lastY = adjustedY
							}
						})

						// Extend line to the right edge of chart
						const lastPoint = line.pathPoints[line.pathPoints.length - 1]
						if (lastPoint && lastPoint.x < padding.left + chartWidth) {
							if (foundSplit) {
								afterParts.push(`H ${padding.left + chartWidth}`)
							} else {
								beforeParts.push(`H ${padding.left + chartWidth}`)
							}
						}

						return (
							<g key={`line-${line.bucketIndex}`}>
								{/* Line before hover */}
								<path
									d={beforeParts.join(" ")}
									fill="none"
									stroke={line.color}
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={LINE_CONFIG.strokeWidth}
								/>
								{/* Line after hover - faded */}
								{afterParts.length > 0 && (
									<path
										d={afterParts.join(" ")}
										fill="none"
										stroke={line.color}
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeOpacity={0.3}
										strokeWidth={LINE_CONFIG.strokeWidth}
									/>
								)}
							</g>
						)
					})}

					{/* Endpoint dots - always show, with pulse animation for live markets */}
					{!hoveredX &&
						chartData.lines.map(line => {
							const lastPt = line.pathPoints[line.pathPoints.length - 1]
							if (!lastPt) {
								return null
							}
							// Endpoints have no X offset (lines end at same spot), only Y offset
							const yOffset = getLineYOffset(line.bucketIndex, buckets.length)
							const dotX = lastPt.x
							const dotY = lastPt.y + yOffset
							return (
								<g key={`end-${line.bucketIndex}`}>
									{/* Pulsing ring for live markets only */}
									{isLive && (
										<circle
											cx={dotX}
											cy={dotY}
											fill="none"
											r={3}
											stroke={line.color}
											strokeOpacity={0.5}
											strokeWidth={1.5}
										>
											<animate
												attributeName="r"
												className="motion-safe:animate-[pulse-r_1.5s_infinite]"
												dur="1.5s"
												from="3"
												repeatCount="indefinite"
												to="10"
											>
												<set
													attributeName="repeatCount"
													media="(prefers-reduced-motion: reduce)"
													to="0"
												/>
											</animate>
											<animate
												attributeName="stroke-opacity"
												className="motion-safe:animate-[pulse-opacity_1.5s_infinite]"
												dur="1.5s"
												from="0.6"
												repeatCount="indefinite"
												to="0"
											>
												<set
													attributeName="repeatCount"
													media="(prefers-reduced-motion: reduce)"
													to="0"
												/>
											</animate>
										</circle>
									)}
									{/* Solid endpoint dot */}
									<circle cx={dotX} cy={dotY} fill={line.color} r={3} />
								</g>
							)
						})}

					{/* Hover interaction - vertical line and floating labels */}
					{hoveredData && (
						<>
							{/* Vertical guideline */}
							<line
								className="stroke-text-muted/50"
								strokeDasharray="4 2"
								strokeWidth={1}
								x1={hoveredData.x}
								x2={hoveredData.x}
								y1={padding.top}
								y2={height - padding.bottom}
							/>

							{/* Timestamp label at top */}
							<g>
								<rect
									className="fill-bg-tertiary stroke-border-subtle"
									height={14}
									rx={3}
									width={56}
									x={Math.min(hoveredData.x - 28, width - padding.right - 28)}
									y={3}
								/>
								<text
									className="fill-text-secondary font-medium text-[8px]"
									dominantBaseline="middle"
									textAnchor="middle"
									x={Math.min(hoveredData.x, width - padding.right)}
									y={10}
								>
									{formatHoverTime(hoveredData.timestampMs)}
								</text>
							</g>

							{/* Dots on each line */}
							{hoveredData.lineData.map(lineItem => (
								<circle
									cx={hoveredData.x}
									cy={lineItem.y}
									fill={lineItem.color}
									key={`hover-dot-${lineItem.bucketIndex}`}
									r={3}
								/>
							))}

							{/* Single tooltip card showing all outcomes - compact Polymarket style */}
							{(() => {
								// Scale down for many outcomes
								const outcomeCount = hoveredData.lineData.length
								const isCompact = outcomeCount > 3
								const rowHeight = isCompact ? 12 : 14
								const cardPadding = isCompact ? 5 : 6
								const cardWidth = isCompact ? 100 : 120
								const dotRadius = isCompact ? 2.5 : 3
								const fontSize = isCompact ? 7 : 8
								const cardHeight = outcomeCount * rowHeight + cardPadding * 2

								// Calculate max chars for name based on available space
								// Leave room for: padding + dot + gap + name + gap + percentage
								const pctWidth = 22 // "100%" at this font size
								const dotSpace = cardPadding + dotRadius * 2 + 4
								const availableWidth = cardWidth - dotSpace - pctWidth - cardPadding
								// Approx 4px per char at 7px font, 4.5px at 8px
								const charWidth = isCompact ? 4 : 4.5
								const maxChars = Math.floor(availableWidth / charWidth)

								// Sort by price descending for display
								const sortedByPrice = [...hoveredData.lineData].sort((a, b) => b.price - a.price)

								// Position card to the right of hover line, or left if near edge
								const isNearRight = hoveredData.x + 8 + cardWidth > width - padding.right
								const cardX = isNearRight ? hoveredData.x - cardWidth - 8 : hoveredData.x + 8

								// Center card vertically in chart, constrained to bounds
								const chartCenterY = padding.top + chartHeight / 2
								let cardY = chartCenterY - cardHeight / 2
								cardY = Math.max(
									padding.top + 18,
									Math.min(cardY, height - padding.bottom - cardHeight),
								)

								return (
									<g>
										{/* Card background */}
										<rect
											className="fill-bg-primary stroke-border-subtle"
											filter="drop-shadow(0px 1px 4px rgba(0,0,0,0.12))"
											height={cardHeight}
											rx={4}
											strokeWidth={0.5}
											width={cardWidth}
											x={cardX}
											y={cardY}
										/>
										{/* Outcome rows */}
										{sortedByPrice.map((lineItem, i) => {
											const rowY = cardY + cardPadding + i * rowHeight + rowHeight / 2
											const pct = lineItem.price * 100
											const pctText = pct < 1 ? `${pct.toFixed(1)}%` : `${Math.round(pct)}%`

											// Truncate long names to prevent overlap
											const fullName = lineItem.bucket.label
											const displayName =
												fullName.length > maxChars
													? `${fullName.slice(0, maxChars - 1)}…`
													: fullName

											return (
												<g key={`tooltip-row-${lineItem.bucketIndex}`}>
													{/* Color dot */}
													<circle
														cx={cardX + cardPadding + dotRadius}
														cy={rowY}
														fill={lineItem.color}
														r={dotRadius}
													/>
													{/* Outcome name - truncated */}
													<text
														className="fill-text-primary"
														dominantBaseline="middle"
														style={{ fontSize: `${fontSize}px` }}
														x={cardX + cardPadding + dotRadius * 2 + 4}
														y={rowY}
													>
														{displayName}
													</text>
													{/* Percentage - right aligned */}
													<text
														className="fill-text-primary font-semibold"
														dominantBaseline="middle"
														style={{ fontSize: `${fontSize}px` }}
														textAnchor="end"
														x={cardX + cardWidth - cardPadding}
														y={rowY}
													>
														{pctText}
													</text>
												</g>
											)
										})}
									</g>
								)
							})()}
						</>
					)}
				</svg>
			</div>
		</div>
	)
})

interface LegendProps {
	buckets: OutcomeBucket[]
	prices: Record<number, number>
	hoveredPrices?: Record<number, number>
}

const Legend = memo(function Legend({ buckets, prices, hoveredPrices }: LegendProps) {
	return (
		<div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 sm:gap-x-5">
			{buckets.map((bucket, i) => {
				const price = hoveredPrices?.[i] ?? prices[i] ?? 0
				const hasPrice = price > 0 || Object.keys(prices).length > 0
				return (
					<div className="flex min-w-0 max-w-full items-center gap-1.5 sm:gap-2" key={bucket.id}>
						<span
							className="size-2.5 shrink-0 rounded-full sm:size-3"
							style={{ backgroundColor: getOutcomeColor(i) }}
						/>
						<span className="min-w-0 truncate text-sm text-text-secondary">{bucket.label}</span>
						<span className="shrink-0 font-semibold text-sm text-text-primary sm:text-lg">
							{hasPrice ? `${(price * 100).toFixed(0)}%` : "--"}
						</span>
					</div>
				)
			})}
		</div>
	)
})

function ChartLegendSkeleton({
	bucketCount = 2,
	labels = [...BINARY_LABELS],
}: {
	bucketCount?: number
	labels?: string[]
}) {
	return (
		<div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1 sm:gap-x-5">
			{Array.from({ length: bucketCount }, (_, i) => (
				<div
					className="flex min-w-0 max-w-full items-center gap-1.5 sm:gap-2"
					key={labels[i] ?? `bucket-${i}`}
				>
					{/* Static colored dot */}
					<span
						className="size-2.5 shrink-0 rounded-full sm:size-3"
						style={{ backgroundColor: CHART_COLORS[i] ?? "#6b7280" }}
					/>
					{/* Static label */}
					<span className="min-w-0 truncate text-sm text-text-secondary">
						{labels[i] ?? `Bucket ${i + 1}`}
					</span>
					{/* Only price needs skeleton - matches font-semibold text-sm sm:text-lg */}
					<Skeleton className="h-5 w-10 shrink-0 sm:h-7 sm:w-12" />
				</div>
			))}
		</div>
	)
}
